/**
 * 25 mock clashes spanning the three federated disciplines.
 * Used by Track 3 — ClashWorkbench + ClashPairCanvas + BCF round-trip.
 */
export type ClashSeverity = "critical" | "major" | "minor";
export type ClashStatus = "open" | "reviewed" | "resolved" | "approved" | "ignored";

export interface MockClash {
  clashId: number;
  testName: string;
  severity: ClashSeverity;
  status: ClashStatus;
  distanceMm: number;
  position: { x: number; y: number; z: number };
  elementA: {
    modelId: number;
    expressId: number;
    globalId: string;
    name: string;
    ifcClass: string;
    discipline: string;
  };
  elementB: {
    modelId: number;
    expressId: number;
    globalId: string;
    name: string;
    ifcClass: string;
    discipline: string;
  };
  assignee?: { userId: number; userName: string };
  detectedAt: string;
  level: string;
}

const guid = (i: number) => `9F8E${i.toString(36).padStart(8, "0").toUpperCase()}CLASH`;

const make = (
  i: number,
  severity: ClashSeverity,
  status: ClashStatus,
  a: Pick<MockClash["elementA"], "ifcClass" | "name" | "discipline" | "modelId">,
  b: Pick<MockClash["elementB"], "ifcClass" | "name" | "discipline" | "modelId">,
  level: string,
  distanceMm: number
): MockClash => ({
  clashId: 60000 + i,
  testName: `T-${a.discipline.split("_")[0].toLowerCase()}-vs-${b.discipline.split("_")[0].toLowerCase()}`,
  severity,
  status,
  distanceMm,
  position: { x: 5 + i * 1.5, y: 3 + (i % 4), z: 2 + (i % 3) * 1.2 },
  elementA: { ...a, expressId: 13000 + i, globalId: guid(i * 2) },
  elementB: { ...b, expressId: 17000 + i, globalId: guid(i * 2 + 1) },
  assignee:
    i % 3 === 0 ? { userId: 7, userName: "Priya Menon" } : { userId: 11, userName: "Layla Hassan" },
  detectedAt: "2026-05-22T08:30:00Z",
  level,
});

export const mockClashes: MockClash[] = [
  make(1, "critical", "open",
    { ifcClass: "IfcColumn", name: "RC Column C-014", discipline: "STRUCTURAL", modelId: 9002 },
    { ifcClass: "IfcDuctSegment", name: "Supply Duct — Zone 3", discipline: "MEP_MECHANICAL", modelId: 9003 },
    "Level 5", 142),
  make(2, "critical", "open",
    { ifcClass: "IfcBeam", name: "Primary Beam B-008", discipline: "STRUCTURAL", modelId: 9002 },
    { ifcClass: "IfcPipeSegment", name: "CHW Pipe 200mm — Run 2", discipline: "MEP_MECHANICAL", modelId: 9003 },
    "Level 5", 88),
  make(3, "major", "reviewed",
    { ifcClass: "IfcWall", name: "External Wall — Type A 4", discipline: "ARCHITECTURAL", modelId: 9001 },
    { ifcClass: "IfcCableCarrierSegment", name: "Cable Tray CT-12", discipline: "MEP_ELECTRICAL", modelId: 9003 },
    "Level 2", 36),
  make(4, "minor", "open",
    { ifcClass: "IfcCeiling", name: "Ceiling Panel CP-44", discipline: "ARCHITECTURAL", modelId: 9001 },
    { ifcClass: "IfcLightFixture", name: "Light Fixture LF-3", discipline: "MEP_ELECTRICAL", modelId: 9003 },
    "Level 2", 12),
  make(5, "critical", "resolved",
    { ifcClass: "IfcSlab", name: "Slab S-024", discipline: "STRUCTURAL", modelId: 9002 },
    { ifcClass: "IfcDuctSegment", name: "Supply Duct — Zone 1", discipline: "MEP_MECHANICAL", modelId: 9003 },
    "Level 4", 220),
  make(6, "major", "open",
    { ifcClass: "IfcWall", name: "Internal Wall — Type B 2", discipline: "ARCHITECTURAL", modelId: 9001 },
    { ifcClass: "IfcPipeSegment", name: "CHW Pipe 150mm — Run 4", discipline: "MEP_MECHANICAL", modelId: 9003 },
    "Level 3", 64),
  make(7, "minor", "approved",
    { ifcClass: "IfcDoor", name: "Door D-3", discipline: "ARCHITECTURAL", modelId: 9001 },
    { ifcClass: "IfcCableCarrierSegment", name: "Cable Tray CT-08", discipline: "MEP_ELECTRICAL", modelId: 9003 },
    "Ground Floor", 8),
  make(8, "critical", "open",
    { ifcClass: "IfcColumn", name: "RC Column C-022", discipline: "STRUCTURAL", modelId: 9002 },
    { ifcClass: "IfcPipeSegment", name: "CHW Pipe 200mm — Run 6", discipline: "MEP_MECHANICAL", modelId: 9003 },
    "Level 7", 175),
  make(9, "major", "open",
    { ifcClass: "IfcBeam", name: "Primary Beam B-014", discipline: "STRUCTURAL", modelId: 9002 },
    { ifcClass: "IfcDuctSegment", name: "Supply Duct — Zone 6", discipline: "MEP_MECHANICAL", modelId: 9003 },
    "Level 8", 92),
  make(10, "minor", "open",
    { ifcClass: "IfcRailing", name: "Stair Railing R-04", discipline: "ARCHITECTURAL", modelId: 9001 },
    { ifcClass: "IfcLightFixture", name: "Light Fixture LF-7", discipline: "MEP_ELECTRICAL", modelId: 9003 },
    "Level 3", 18),
  ...Array.from({ length: 15 }, (_, k) =>
    make(11 + k,
      (["critical", "major", "minor"] as ClashSeverity[])[k % 3],
      (["open", "reviewed", "resolved"] as ClashStatus[])[k % 3],
      { ifcClass: "IfcBeam", name: `Secondary Beam SB-${k + 1}`, discipline: "STRUCTURAL", modelId: 9002 },
      { ifcClass: "IfcPipeSegment", name: `Drain Pipe DP-${k + 1}`, discipline: "MEP_PLUMBING", modelId: 9003 },
      `Level ${(k % 10) + 1}`,
      40 + k * 6
    )
  ),
];

export const mockClashSummary = {
  projectId: 42,
  total: mockClashes.length,
  critical: mockClashes.filter((c) => c.severity === "critical").length,
  major: mockClashes.filter((c) => c.severity === "major").length,
  minor: mockClashes.filter((c) => c.severity === "minor").length,
  open: mockClashes.filter((c) => c.status === "open").length,
  reviewed: mockClashes.filter((c) => c.status === "reviewed").length,
  resolved: mockClashes.filter((c) => c.status === "resolved").length,
  approved: mockClashes.filter((c) => c.status === "approved").length,
};
